import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { requestsAPI, adminAPI, manifestReceivingAPI, shippingAPI } from '../../../services/api';
import { formatDate, formatStatus } from '../../../utils/formatters';
import { toast } from 'react-toastify';

// Components
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import DocumentsTab from '../../../components/DocumentsTab';
import RequestBillingManager from '../../../components/RequestBillingManager';
import PhotosTab from '../../../components/PhotosTab';
import SuccessAlert from '../../../components/SuccessAlert';
import StatusBadge from '../../../components/StatusBadge';
import RequestStatusUpdater from '../../../components/RequestStatusUpdater';
import RequestCostsManager from '../../../components/RequestCostsManager';
import RequestItemsSection from '../../../components/RequestItemsSection';
import CrossDockRequestDetail from './CrossDockRequestDetail';
import ShipmentPanel from '../../../components/ShipmentPanel';
import ManifestReceivingModal from '../../../components/ManifestReceivingModal';
import ActivityTimeline from '../../../components/ActivityTimeline';
import RequestCommentsPanel from '../../../components/RequestCommentsPanel';
import ReceivingHistoryPanel from '../../../components/ReceivingHistoryPanel';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';

const getReceivingServiceLabels = (instructions = {}) => {
  const labels = [];
  if (instructions.require_serial_numbers_on_receive) labels.push('Record serial numbers on receipt');
  if (instructions.require_mac_addresses_on_receive) labels.push('Record MAC addresses on receipt');
  if (instructions.relabel_on_receive) labels.push('Relabel units on receipt');
  return labels;
};

const formatShippingMethod = (method, fallback = '-') => {
  const labels = {
    courier: 'Courier',
    freight: 'Freight',
    pickup: 'Pickup'
  };
  return labels[method] || fallback;
};

const compactList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const formatLineProject = (line) => {
  const parts = [line?.project_code, line?.project_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' / ') : null;
};

const formatRequestedIdentifiers = (line) => {
  const serials = compactList(line?.requested_serials).map((value) => `SN ${value}`);
  const macs = compactList(line?.requested_macs).map((value) => `MAC ${value}`);
  const assets = compactList(line?.requested_asset_ids).map((value) => `Asset ${value}`);
  return [...serials, ...macs, ...assets];
};

const formatAssetIdentifier = (asset) => {
  if (!asset) return '-';
  return [asset.serial_number, asset.mac_address].filter(Boolean).join(' / ') || asset.id || '-';
};

const formatReservationSource = (source) => {
  const labels = {
    requested_identifier: 'Requested identifier',
    project_fifo: 'Project FIFO',
    fifo_non_project: 'FIFO non-project',
    project_stock_fallback: 'Project-stock fallback',
    package_fifo: 'FIFO'
  };
  return labels[source] || null;
};

const getPackageItemCount = (pkg) => (
  (pkg?.LineItems || []).reduce((total, line) => total + Number(line.quantity || 0), 0)
);

const formatPackageMeasurements = (pkg) => (
  `${pkg.package_type || 'box'} · ${pkg.weight_lbs || '-'} lb · ${pkg.length || '-'} x ${pkg.width || '-'} x ${pkg.height || '-'} in`
);

const getShipmentPackageCount = (shipment) => (shipment?.Packages || []).length;

const getShipmentItemCount = (shipment) => (
  (shipment?.LineItems || []).reduce((total, line) => total + Number(line.quantity_to_ship || line.quantity || 0), 0)
);

const positiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getInventoryItem = (line) => line?.InventoryItem || {};

const getLineUnitWeight = (line) => {
  const item = getInventoryItem(line);
  return positiveNumber(line?.item_weight)
    || positiveNumber(line?.weight)
    || positiveNumber(item.weight_lbs)
    || positiveNumber(line?.carton_weight)
    || positiveNumber(item.carton_weight);
};

const getLineDimensions = (line) => {
  const item = getInventoryItem(line);
  const dimensions = line?.dimensions || {};
  return {
    length: positiveNumber(line?.item_length)
      || positiveNumber(line?.carton_length)
      || positiveNumber(dimensions.length)
      || positiveNumber(item.length)
      || positiveNumber(item.carton_length),
    width: positiveNumber(line?.item_width)
      || positiveNumber(line?.carton_width)
      || positiveNumber(dimensions.width)
      || positiveNumber(item.width)
      || positiveNumber(item.carton_width),
    height: positiveNumber(line?.item_height)
      || positiveNumber(line?.carton_height)
      || positiveNumber(dimensions.height)
      || positiveNumber(item.height)
      || positiveNumber(item.carton_height)
  };
};

const roundPackageNumber = (value) => {
  if (!Number.isFinite(value) || value <= 0) return '';
  return String(Math.ceil(value * 100) / 100);
};

const buildPackageSuggestion = (lines, quantities) => {
  const selectedLines = lines
    .map((line) => ({
      line,
      quantity: Number(quantities[line.id] || 0)
    }))
    .filter(({ quantity }) => quantity > 0);

  let totalWeight = 0;
  const suggestedDimensions = { length: 0, width: 0, height: 0 };
  const missingWeight = [];
  const missingDimensions = [];

  selectedLines.forEach(({ line, quantity }) => {
    const sku = line.productSku || line.InventoryItem?.product_sku || 'SKU';
    const unitWeight = getLineUnitWeight(line);
    const dimensions = getLineDimensions(line);

    if (unitWeight) {
      totalWeight += unitWeight * quantity;
    } else {
      missingWeight.push(sku);
    }

    if (dimensions.length && dimensions.width && dimensions.height) {
      suggestedDimensions.length = Math.max(suggestedDimensions.length, dimensions.length);
      suggestedDimensions.width = Math.max(suggestedDimensions.width, dimensions.width);
      suggestedDimensions.height = Math.max(suggestedDimensions.height, dimensions.height);
    } else {
      missingDimensions.push(sku);
    }
  });

  return {
    selectedCount: selectedLines.length,
    weight_lbs: roundPackageNumber(totalWeight),
    length: roundPackageNumber(suggestedDimensions.length),
    width: roundPackageNumber(suggestedDimensions.width),
    height: roundPackageNumber(suggestedDimensions.height),
    missingWeight,
    missingDimensions
  };
};

const AdminRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReceivingModal, setShowReceivingModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [packageQuantities, setPackageQuantities] = useState({});
  const [packageDetails, setPackageDetails] = useState({
    package_type: 'box',
    weight_lbs: '',
    length: '',
    width: '',
    height: ''
  });
  const [selectedShipmentPackageIds, setSelectedShipmentPackageIds] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editPackageDetails, setEditPackageDetails] = useState({
    package_label: '',
    package_type: 'box',
    weight_lbs: '',
    length: '',
    width: '',
    height: ''
  });
  const [cancelReason, setCancelReason] = useState('');
  
  // Clear any cached data for this request when component mounts
  useEffect(() => {
    queryClient.removeQueries(['request', id]);
    queryClient.invalidateQueries(['request', id]);
  }, [id, queryClient]);

  useEffect(() => {
    const handleDocumentClick = () => setShowActionsMenu(false);
    if (showActionsMenu) {
      document.addEventListener('click', handleDocumentClick);
    }
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [showActionsMenu]);
  
  // Fetch request details
  const { 
    data: request, 
    isLoading, 
    error,
    refetch 
  } = useQuery(
    ['request', id], 
    () => adminAPI.getRequest(id),
    {
      retry: 2,
      enabled: !!id, // Only run if we have an ID
      refetchOnMount: true, // Always refetch when component mounts
      refetchOnWindowFocus: false,
      cacheTime: 0, // Don't cache
      staleTime: 0, // Always consider stale
      onError: (err) => {
        console.error('Error fetching request:', err);
      },
      onSuccess: () => {
      }
    }
  );

  // Check if request can be deleted
  const { 
    data: deleteStatus 
  } = useQuery(
    ['canDeleteRequest', id],
    () => adminAPI.checkCanDeleteRequest(id),
    {
      enabled: !!id && !!request && request.requestType === 'incoming',
      refetchOnWindowFocus: false
    }
  );
  
  // Delete request mutation
  const deleteRequestMutation = useMutation({
    mutationFn: (requestId) => adminAPI.deleteRequest(requestId),
    onSuccess: () => {
      toast.success('Request deleted successfully');
      navigate('/admin/requests');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error deleting request');
    }
  });

  const cancelRequestMutation = useMutation({
    mutationFn: ({ requestId, reason }) => adminAPI.cancelRequest(requestId, { reason }),
    onSuccess: () => {
      toast.success('Request cancelled successfully');
      setShowActionsMenu(false);
      setShowCancelModal(false);
      setCancelReason('');
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error cancelling request');
    }
  });

  // Handle status update
  const handleStatusUpdate = (newStatus) => {
    setStatusUpdateSuccess(true);
    refetch(); // Refresh the request data
    
    // Clear success message after a delay
    setTimeout(() => {
      setStatusUpdateSuccess(false);
    }, 3000);
  };
  
  // Check if this is an inbound_manifest request and fetch manifest data
  const { data: manifestData } = useQuery(
    ['manifest-for-request', id],
    async () => {
      // Find the manifest that has this request's ID as its request_id
      const response = await fetch(`/api/admin/manifests?request_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      if (!response.ok) throw new Error('Failed to fetch manifest');
      const data = await response.json();
      if (data.data && data.data.length > 0) {
        return { manifest: data.data[0] };
      }
      return null;
    },
    {
      enabled: !!(request?.requestType === 'inbound_manifest'),
      refetchOnWindowFocus: false,
      retry: 2
    }
  );

  // Force manifest query to refetch when request data changes
  useEffect(() => {
    if (request?.requestType === 'inbound_manifest') {
      queryClient.invalidateQueries(['manifest-for-request', id]);
    }
  }, [request, id, queryClient]);

  // Manifest approval mutations
  const approveManifestMutation = useMutation(
    ({ manifestId, notes }) => manifestReceivingAPI.approveManifest(manifestId, notes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['manifest-for-request', id]);
        queryClient.invalidateQueries(['request', id]);
        toast.success('Manifest approved successfully');
      },
      onError: (error) => {
        toast.error('Failed to approve manifest: ' + error.message);
      }
    }
  );

  const denyManifestMutation = useMutation(
    ({ manifestId, notes }) => manifestReceivingAPI.denyManifest(manifestId, notes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['manifest-for-request', id]);
        queryClient.invalidateQueries(['request', id]);
        toast.success('Manifest denied');
      },
      onError: (error) => {
        toast.error('Failed to deny manifest: ' + error.message);
      }
    }
  );

  // Handle invoice created
  const handleInvoiceCreated = (invoice) => {
    setInvoiceCreated(true);
    refetch(); // Refresh the request data
    
    // Clear success message after a delay
    setTimeout(() => {
      setInvoiceCreated(false);
    }, 3000);
  };
  const receivingServiceLabels = getReceivingServiceLabels(
    request?.receivingInstructions || request?.receiving_instructions || {}
  );
  const isRequestType = (type) => request?.requestType === type;
  const requestLineItems = request?.RequestLineItems || [];
  const { data: outboundPackagesData } = useQuery(
    ['outbound-packages', id],
    () => shippingAPI.getRequestPackages(id),
    {
      enabled: !!id && isRequestType('ship'),
      refetchOnWindowFocus: false
    }
  );
  const outboundPackages = outboundPackagesData?.packages || [];
  const hasOutboundPackages = outboundPackages.length > 0;
  const createPackageMutation = useMutation(
    (payload) => shippingAPI.createRequestPackage(request.id, payload),
    {
      onSuccess: () => {
        toast.success('Package created successfully');
        setShowPackageModal(false);
        setActiveTab('packages');
        queryClient.invalidateQueries(['outbound-packages', id]);
        queryClient.invalidateQueries(['request', id]);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create package');
      }
    }
  );
  const createShipmentFromPackagesMutation = useMutation(
    (packageIds) => shippingAPI.createShipmentFromPackages(request.id, packageIds),
    {
      onSuccess: () => {
        const shippingMethod = request.shippingMethod || request.shipping_method;
        toast.success(
          shippingMethod === 'pickup'
            ? 'Pickup shipment created successfully'
            : 'Shipment created. Use the standard shipping portal to book carrier service.'
        );
        setActiveTab('shipment');
        queryClient.invalidateQueries(['outbound-packages', id]);
        queryClient.invalidateQueries(['request', id]);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create shipment');
      }
    }
  );
  const cancelPackageMutation = useMutation(
    ({ packageId, reason }) => shippingAPI.cancelRequestPackage(request.id, packageId, reason),
    {
      onSuccess: () => {
        toast.success('Package cancelled and inventory released');
        queryClient.invalidateQueries(['outbound-packages', id]);
        queryClient.invalidateQueries(['request', id]);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to cancel package');
      }
    }
  );
  const updatePackageMutation = useMutation(
    ({ packageId, payload }) => shippingAPI.updateRequestPackage(request.id, packageId, payload),
    {
      onSuccess: () => {
        toast.success('Package updated successfully');
        setEditingPackage(null);
        queryClient.invalidateQueries(['outbound-packages', id]);
        queryClient.invalidateQueries(['request', id]);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update package');
      }
    }
  );
  const updatePackageLineMutation = useMutation(
    ({ packageId, lineItemId, payload }) => shippingAPI.updateRequestPackageLine(request.id, packageId, lineItemId, payload),
    {
      onSuccess: () => {
        toast.success('Package line updated and inventory released');
        queryClient.invalidateQueries(['outbound-packages', id]);
        queryClient.invalidateQueries(['request', id]);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update package line');
      }
    }
  );
  const packagedQtyByLineId = outboundPackages.reduce((acc, pkg) => {
    if (pkg.status === 'cancelled') return acc;
    (pkg.LineItems || []).forEach((line) => {
      acc[line.request_line_item_id] = (acc[line.request_line_item_id] || 0) + Number(line.quantity || 0);
    });
    return acc;
  }, {});
  const hasRemainingToPackage = requestLineItems.some((line) => {
    const requestedQty = Number(line.quantity || 0);
    const packagedQty = Number(packagedQtyByLineId[line.id] || 0);
    return requestedQty > packagedQty;
  });
  const openPackageModal = () => {
    if (!hasRemainingToPackage) {
      toast.info('All items are already packaged');
      return;
    }

    const initial = {};
    requestLineItems.forEach((line) => {
      const remaining = Math.max(0, Number(line.quantity || 0) - Number(packagedQtyByLineId[line.id] || 0));
      initial[line.id] = remaining;
    });
    setPackageQuantities(initial);
    setPackageDetails({
      package_type: 'box',
      weight_lbs: '',
      length: '',
      width: '',
      height: ''
    });
    setShowPackageModal(true);
  };
  const openEditPackageModal = (pkg) => {
    setEditingPackage(pkg);
    setEditPackageDetails({
      package_label: pkg.package_label || `Package ${pkg.package_number}`,
      package_type: pkg.package_type || 'box',
      weight_lbs: pkg.weight_lbs ?? '',
      length: pkg.length ?? '',
      width: pkg.width ?? '',
      height: pkg.height ?? ''
    });
  };
  const handlePrintPickList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/admin/requests/${request.id}/pick-list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const html = await response.text();
      const win = window.open('', '_blank');
      if (win) { win.document.write(html); win.document.close(); }
    } catch (err) {
      toast.error('Failed to load pick list');
    }
  };
  const hasShipmentGroups = isRequestType('ship') && Array.isArray(request?.shipmentGroups) && request.shipmentGroups.length > 0;
  const isInboundRequest = isRequestType('incoming') || isRequestType('inbound_manifest');
  const requestShipments = Array.isArray(request?.Shipments) && request.Shipments.length > 0
    ? request.Shipments
    : (request?.Shipment ? [request.Shipment] : []);
  const hasShipment = requestShipments.length > 0 && (isRequestType('ship') || isRequestType('pick_pack') || isRequestType('kit_build'));
  const shippablePackages = outboundPackages.filter((pkg) => pkg.status === 'packed' && !pkg.shipment_id);
  const shippablePackageIds = shippablePackages.map((pkg) => pkg.id);
  const shippablePackageIdsKey = shippablePackageIds.join('|');
  const hasShippablePackages = shippablePackages.length > 0;
  const isPickupRequest = (request?.shippingMethod || request?.shipping_method) === 'pickup';
  const packageHasCarrierDetails = (pkg) => Number(pkg.weight_lbs || 0) > 0
    && Number(pkg.length || 0) > 0
    && Number(pkg.width || 0) > 0
    && Number(pkg.height || 0) > 0;
  const shippablePackagesMissingCarrierDetails = isPickupRequest
    ? []
    : shippablePackages.filter((pkg) => !packageHasCarrierDetails(pkg));
  const selectedShippablePackages = shippablePackages.filter((pkg) => selectedShipmentPackageIds.includes(pkg.id));
  const selectedPackagesMissingCarrierDetails = isPickupRequest
    ? []
    : selectedShippablePackages.filter((pkg) => !packageHasCarrierDetails(pkg));
  const selectedShipmentPackageCount = selectedShippablePackages.length;
  const selectedShipmentItemCount = selectedShippablePackages.reduce((total, pkg) => total + getPackageItemCount(pkg), 0);
  const packageSuggestion = buildPackageSuggestion(requestLineItems, packageQuantities);
  const hasShippingContent = hasShipmentGroups || hasShipment || hasOutboundPackages;
  const editableRequestStatuses = ['draft', 'pending', 'pending_approval', 'approved', 'pending_receive', 'in_progress'];
  const canEditRequest = request && editableRequestStatuses.includes(request.status);
  const canDeleteIncomingRequest = request?.requestType === 'incoming';
  const requestsBreadcrumbLink = isRequestType('ship')
    ? '/admin/requests?type=ship'
    : '/admin/requests?type=incoming,crossdock,inbound_manifest';
  const requestsBreadcrumbLabel = isRequestType('ship') ? 'Outbound' : 'Inbound';
  const canCreatePackage = request?.requestType === 'ship'
    && ['pending', 'approved', 'in_progress'].includes(request?.status)
    && hasRemainingToPackage;
  const canPrintPickList = request?.requestType === 'ship';
  const canCreateShipmentFromPackages = request?.requestType === 'ship'
    && selectedShippablePackages.length > 0
    && (isPickupRequest || selectedPackagesMissingCarrierDetails.length === 0);
  const canStartReceivingIncoming = request?.status === 'pending_receive' && request?.requestType === 'incoming';
  const canStartReceivingManifest = request?.requestType === 'inbound_manifest'
    && manifestData?.manifest
    && (manifestData.manifest.status === 'pending_receipt' || manifestData.manifest.status === 'partially_received');
  const canReviewManifest = request?.requestType === 'inbound_manifest'
    && manifestData?.manifest?.status === 'pending_approval';
  const hasAvailableActions = !!(
    canCreatePackage
    || canPrintPickList
    || canStartReceivingManifest
    || canStartReceivingIncoming
    || canEditRequest
    || ['draft', 'pending', 'pending_approval', 'approved'].includes(request?.status)
    || canReviewManifest
    || canDeleteIncomingRequest
  );

  useEffect(() => {
    const currentShippablePackageIds = shippablePackageIdsKey ? shippablePackageIdsKey.split('|') : [];
    setSelectedShipmentPackageIds((current) => {
      const stillValid = current.filter((packageId) => currentShippablePackageIds.includes(packageId));
      const next = Array.from(new Set([...stillValid, ...currentShippablePackageIds]));
      if (next.length === current.length && next.every((packageId, index) => packageId === current[index])) {
        return current;
      }
      return next;
    });
  }, [shippablePackageIdsKey]);
  
  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error.message || 'Error loading request'} />;
  if (!request) return <ErrorAlert message="Request not found or access denied" />;

  // Use specialized component for cross-dock requests
  if (request?.requestType === 'crossdock') {
    return <CrossDockRequestDetail />;
  }
  
  // Format address if it exists
  const formatAddress = (address) => {
    if (!address || (!address.street && !address.city && !address.state)) {
      return '-';
    }
    
    return (
      <>
        <div>{address.street || '-'}</div>
        <div>{address.city || '-'}, {address.state || '-'} {address.postalCode || ''}</div>
        <div>{address.country || '-'}</div>
      </>
    );
  };
  
  return (
    <div className="px-4 py-8 sm:px-6 lg:px-8 max-w-4xl mx-auto">
      {statusUpdateSuccess && (
        <SuccessAlert message="Request status updated successfully" />
      )}
      
      {invoiceCreated && (
        <SuccessAlert message="Invoice created successfully" />
      )}
      
      <div className="mb-4 flex items-center text-sm text-gray-500">
        <Link to="/admin" className="hover:text-gray-700">Dashboard</Link>
        <FiChevronRight className="mx-2 h-4 w-4 text-gray-400" />
        <Link to={requestsBreadcrumbLink} className="hover:text-gray-700">{requestsBreadcrumbLabel}</Link>
        <FiChevronRight className="mx-2 h-4 w-4 text-gray-400" />
        <span className="text-gray-900">{request?.requestNumber ? request.requestNumber.split('-').slice(0, 2).join('-') : 'Request Detail'}</span>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Request {request?.requestNumber ? request.requestNumber.split('-').slice(0, 2).join('-') : '-'} - {request?.Company?.name || request?.Company?.companyName || '-'}
        </h1>
        <div className="flex items-center gap-3">
          {canPrintPickList && (
            <button
              type="button"
              onClick={handlePrintPickList}
              className="inline-flex items-center rounded-md border border-brand-navy bg-brand-navy px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-navy/90"
            >
              Print Pick List
            </button>
          )}
          <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowActionsMenu((prev) => !prev);
            }}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Actions
            <FiChevronDown className="ml-2 h-4 w-4" />
          </button>
          {showActionsMenu && (
            <div
              className="absolute right-0 z-10 mt-2 w-56 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {canStartReceivingManifest && (
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    navigate(`/admin/manifests/${manifestData.manifest.id}/receive`);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm font-medium text-green-800 bg-green-50 hover:bg-green-100"
                >
                  Start Receiving
                </button>
              )}

              {canStartReceivingIncoming && (
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    navigate(`/admin/receiving/${request.id}`);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm font-medium text-green-800 bg-green-50 hover:bg-green-100"
                >
                  Receive Items
                </button>
              )}

              {canCreatePackage && (
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    openPackageModal();
                  }}
                  className="block w-full px-4 py-2 text-left text-sm font-medium text-green-800 bg-green-50 hover:bg-green-100"
                >
                  Create Package
                </button>
              )}

              {request?.requestType === 'ship' && request?.shipmentGroups?.length > 0 && (
                <button
                  type="button"
                  onClick={async () => {
                    setShowActionsMenu(false);
                    try {
                      const html = await adminAPI.getShipmentLabels(request.id);
                      const win = window.open('', '_blank');
                      if (win) { win.document.write(html); win.document.close(); }
                    } catch (err) {
                      toast.error('Failed to load labels');
                    }
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  Print Labels
                </button>
              )}

              {canEditRequest && (
                <Link
                  to={`/admin/requests/${request.id}/edit`}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowActionsMenu(false)}
                >
                  Edit Request
                </Link>
              )}

              {['draft', 'pending', 'pending_approval', 'approved'].includes(request?.status) && (
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    setShowCancelModal(true);
                  }}
                  disabled={cancelRequestMutation.isLoading}
                  className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  {cancelRequestMutation.isLoading ? 'Cancelling...' : 'Cancel Request'}
                </button>
              )}

              {canReviewManifest && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setShowActionsMenu(false);
                      const notes = prompt('Optional approval notes:');
                      approveManifestMutation.mutate({
                        manifestId: manifestData.manifest.id,
                        notes: notes || null
                      });
                    }}
                    disabled={approveManifestMutation.isLoading}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    {approveManifestMutation.isLoading ? 'Approving...' : 'Approve Manifest'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowActionsMenu(false);
                      const reason = prompt('Please provide a reason for denying this manifest:');
                      if (reason && reason.trim()) {
                        denyManifestMutation.mutate({
                          manifestId: manifestData.manifest.id,
                          notes: reason.trim()
                        });
                      }
                    }}
                    disabled={denyManifestMutation.isLoading}
                    className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    {denyManifestMutation.isLoading ? 'Denying...' : 'Deny Manifest'}
                  </button>
                </>
              )}

              {canDeleteIncomingRequest && (
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    setShowDeleteConfirm(true);
                  }}
                  disabled={!deleteStatus?.canDelete || deleteRequestMutation.isLoading}
                  title={deleteStatus?.canDelete ? 'Delete Request' : deleteStatus?.reason || 'Cannot delete request'}
                  className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  {deleteRequestMutation.isLoading ? 'Deleting...' : 'Delete Request'}
                </button>
              )}
              {!hasAvailableActions && (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No actions available
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="mb-6">
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">Select a tab</label>
          <select
            id="tabs"
            name="tabs"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
          >
            <option value="details">Details</option>
            {hasOutboundPackages && <option value="packages">Packages</option>}
            {hasShipment && <option value="shipment">Shipment</option>}
            {hasShipmentGroups && <option value="shipment_groups">Shipment Groups</option>}
            {isInboundRequest && <option value="receiving_history">Receiving History</option>}
            <option value="documents">Documents</option>
            <option value="billing">Billing</option>
            <option value="comments">Comments</option>
            <option value="timeline">Timeline</option>
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`${
                  activeTab === 'details'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Request Details
              </button>
              {hasOutboundPackages && (
                <button
                  onClick={() => setActiveTab('packages')}
                  className={`${
                    activeTab === 'packages'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Packages
                </button>
              )}
              {hasShipment && (
                <button
                  onClick={() => setActiveTab('shipment')}
                  className={`${
                    activeTab === 'shipment'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Shipment
                </button>
              )}
              {hasShipmentGroups && (
                <button
                  onClick={() => setActiveTab('shipment_groups')}
                  className={`${
                    activeTab === 'shipment_groups'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Shipment Groups
                </button>
              )}
              {isInboundRequest && (
                <button
                  onClick={() => setActiveTab('receiving_history')}
                  className={`${
                    activeTab === 'receiving_history'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Receiving History
                </button>
              )}
              <button
                onClick={() => setActiveTab('documents')}
                className={`${
                  activeTab === 'documents'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Documents
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`${
                  activeTab === 'billing'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Billing
              </button>
              <button
                onClick={() => setActiveTab('comments')}
                className={`${
                  activeTab === 'comments'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Comments
              </button>
              <button
                onClick={() => setActiveTab('timeline')}
                className={`${
                  activeTab === 'timeline'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Timeline
              </button>
            </nav>
          </div>
        </div>
      </div>
      
      {/* Details Tab */}
      {activeTab === 'details' && (
        <>
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Request Information
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Request details submitted by the customer.
                </p>
                {request?.requestType === 'inbound_manifest' && manifestData?.manifest?.name && (
                  <p className="mt-2 text-sm font-medium text-gray-700">
                    Manifest Name: {manifestData.manifest.name}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-4">
                <StatusBadge status={request.status} requestType={request.requestType} />
                {request.status === 'pending_receive' && request.requestType === 'incoming' && (
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => {
                      navigate(`/admin/receiving/${request.id}`);
                    }}
                  >
                    <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012-2m-6 9l2 2 4-4" />
                    </svg>
                    Receive Items
                  </button>
                )}
              </div>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Customer
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {request?.Company?.name || request?.Company?.companyName || '-'}
                  </dd>
                </div>
                
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Request Type
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {request?.requestType === 'incoming' && 'Incoming Inventory'}
                    {request?.requestType === 'ship' && 'Ship Products'}
                    {request?.requestType === 'crossdock' && 'Cross-Dock'}
                    {request?.requestType === 'pick_pack' && 'Pick & Pack'}
                    {request?.requestType === 'kit_build' && 'Kit Building'}
                    {request?.requestType === 'inbound_manifest' && 'Inbound Manifest (BOM)'}
                    {!request?.requestType && '-'}
                  </dd>
                </div>
                
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Request Number
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {request?.requestNumber ? request.requestNumber.split('-').slice(0, 2).join('-') : '-'}
                  </dd>
                </div>
                
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Request Date
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {request?.createdAt ? formatDate(request.createdAt) : '-'}
                  </dd>
                </div>
                
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Scheduled Date
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {request?.scheduledDate ? formatDate(request.scheduledDate) : '-'}
                  </dd>
                </div>
                {request?.requestType === 'incoming' && (
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Receiving Instructions
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {receivingServiceLabels.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {receivingServiceLabels.map((label) => (
                            <span key={label} className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                              {label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </dd>
                  </div>
                )}

                {/* Manifest-specific details */}
                {request?.requestType === 'inbound_manifest' && manifestData?.manifest && (
                  <>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">
                        Manifest Name
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {manifestData.manifest.name || '-'}
                      </dd>
                    </div>

                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">
                        Vendor/Supplier
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {manifestData.manifest.vendor || '-'}
                      </dd>
                    </div>

                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">
                        Expected Arrival
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {manifestData.manifest.expected_arrival_date ? formatDate(manifestData.manifest.expected_arrival_date) : '-'}
                      </dd>
                    </div>

                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">
                        Manifest Notes
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap sm:mt-0 sm:col-span-2">
                        {manifestData.manifest.notes || request.specialInstructions || '-'}
                      </dd>
                    </div>

                    {manifestData.manifest.approved_by && (
                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">
                          Approved By
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {manifestData.manifest.ApprovedBy?.name || manifestData.manifest.approved_by}
                          {manifestData.manifest.approved_at && (
                            <div className="text-xs text-gray-500">
                              on {formatDate(manifestData.manifest.approved_at)}
                            </div>
                          )}
                          {manifestData.manifest.approval_notes && (
                            <div className="text-sm text-gray-600 mt-1">
                              "{manifestData.manifest.approval_notes}"
                            </div>
                          )}
                        </dd>
                      </div>
                    )}

                    {manifestData.manifest.denied_by && (
                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">
                          Denied By
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {manifestData.manifest.DeniedBy?.name || manifestData.manifest.denied_by}
                          {manifestData.manifest.denied_at && (
                            <div className="text-xs text-gray-500">
                              on {formatDate(manifestData.manifest.denied_at)}
                            </div>
                          )}
                          {manifestData.manifest.approval_notes && (
                            <div className="text-sm text-red-600 mt-1">
                              Reason: "{manifestData.manifest.approval_notes}"
                            </div>
                          )}
                        </dd>
                      </div>
                    )}

                  </>
                )}
                
                {request.requestType === 'ship' && (
                  <>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">
                        Destination Address
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {formatAddress(request.destinationAddress)}
                      </dd>
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">
                        Shipping Method
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {formatShippingMethod(request.shippingMethod)}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">
                        Special Services
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {(request.liftgate_required || request.liftgateRequired) && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                            Liftgate Required
                          </span>
                        )}
                        {(request.white_glove || request.whiteGloveService) && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-2">
                            White Glove Service
                          </span>
                        )}
                        {request.inside_delivery && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            Inside Delivery
                          </span>
                        )}
                        {!(request.liftgate_required || request.liftgateRequired)
                          && !(request.white_glove || request.whiteGloveService)
                          && !request.inside_delivery
                          && '-'}
                      </dd>
                    </div>
                  </>
                )}
                
                {request.trackingNumber && !hasShipment && (
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Tracking Number
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {request.trackingNumber}
                    </dd>
                  </div>
                )}
                
                <div className={`${request.trackingNumber ? 'bg-white' : 'bg-gray-50'} px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}>
                  <dt className="text-sm font-medium text-gray-500">
                    Special Instructions
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {request.specialInstructions || '-'}
                  </dd>
                </div>
                
                {/* Kit-specific fields */}
                {request.requestType === 'kit_build' && request.kitName && (
                  <>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">
                        Kit Name
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {request.kitName}
                      </dd>
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">
                        Kit Quantity
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {request.kitQuantity || 1}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>

          {/* Request Items */}
          <RequestItemsSection request={request} embedded={true} />
        </>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4" role="dialog" aria-modal="true" aria-labelledby="cancel-request-modal-title">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 id="cancel-request-modal-title" className="text-lg font-semibold text-gray-900">
                Cancel Request
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Provide the internal cancellation reason. This will be included in the customer notification.
              </p>
            </div>
            <div className="px-6 py-4">
              <label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-700">
                Cancellation Reason
              </label>
              <textarea
                id="cancel-reason"
                rows={4}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter the reason for cancelling this request"
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="button"
                disabled={cancelRequestMutation.isLoading}
                onClick={() => {
                  const reason = cancelReason.trim();
                  if (!reason) {
                    toast.error('Cancellation reason is required');
                    return;
                  }
                  cancelRequestMutation.mutate({
                    requestId: request.id,
                    reason
                  });
                }}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
              >
                {cancelRequestMutation.isLoading ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPackageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4" role="dialog" aria-modal="true" aria-labelledby="create-package-modal-title">
          <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 id="create-package-modal-title" className="text-lg font-semibold text-gray-900">
                Create Package
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Confirm the quantities picked for this package. Inventory will be reserved for pending shipment.
              </p>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="text-sm font-semibold text-gray-900">Package Details</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Capture physical package details before carrier shipment. These are optional for customer pickup.
                </p>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-5">
                  <div className="sm:col-span-1">
                    <label htmlFor="package-type" className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                      Type
                    </label>
                    <select
                      id="package-type"
                      value={packageDetails.package_type}
                      onChange={(e) => setPackageDetails((current) => ({
                        ...current,
                        package_type: e.target.value
                      }))}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="box">Box</option>
                      <option value="pallet">Pallet</option>
                      <option value="envelope">Envelope</option>
                      <option value="tube">Tube</option>
                      <option value="crate">Crate</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  {[
                    ['weight_lbs', 'Weight (lb)'],
                    ['length', 'Length (in)'],
                    ['width', 'Width (in)'],
                    ['height', 'Height (in)']
                  ].map(([field, label]) => (
                    <div key={field}>
                      <label htmlFor={`package-${field}`} className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                        {label}
                      </label>
                      <input
                        id={`package-${field}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={packageDetails[field]}
                        onChange={(e) => setPackageDetails((current) => ({
                          ...current,
                          [field]: e.target.value
                        }))}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                  ))}
                </div>
                {packageSuggestion.selectedCount > 0 && (
                  <div className="mt-4 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-950">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="font-medium">Suggested package details</p>
                        <p className="mt-1 text-blue-900">
                          {packageSuggestion.weight_lbs || '-'} lb · {packageSuggestion.length || '-'} x {packageSuggestion.width || '-'} x {packageSuggestion.height || '-'} in
                        </p>
                        <p className="mt-1 text-xs text-blue-800">
                          Based on selected item data. Verify against the actual packed box before creating a carrier shipment.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPackageDetails((current) => ({
                          ...current,
                          weight_lbs: packageSuggestion.weight_lbs || current.weight_lbs,
                          length: packageSuggestion.length || current.length,
                          width: packageSuggestion.width || current.width,
                          height: packageSuggestion.height || current.height
                        }))}
                        className="inline-flex items-center justify-center rounded-md border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-800 hover:bg-blue-100"
                      >
                        Use Suggestion
                      </button>
                    </div>
                    {(packageSuggestion.missingWeight.length > 0 || packageSuggestion.missingDimensions.length > 0) && (
                      <div className="mt-2 space-y-1 text-xs text-amber-800">
                        {packageSuggestion.missingWeight.length > 0 && (
                          <p>Missing weight source data: {packageSuggestion.missingWeight.join(', ')}</p>
                        )}
                        {packageSuggestion.missingDimensions.length > 0 && (
                          <p>Missing dimension source data: {packageSuggestion.missingDimensions.join(', ')}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Item</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Requested</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Remaining</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Package Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {requestLineItems.map((line) => {
                      const packagedQty = Number(packagedQtyByLineId[line.id] || 0);
                      const requestedQty = Number(line.quantity || 0);
                      const remainingQty = Math.max(0, requestedQty - packagedQty);
                      const requestedIdentifiers = formatRequestedIdentifiers(line);
                      const projectLabel = formatLineProject(line);
                      const lineHasWeight = !!getLineUnitWeight(line);
                      const lineDimensions = getLineDimensions(line);
                      const lineHasDimensions = !!(lineDimensions.length && lineDimensions.width && lineDimensions.height);
                      return (
                        <tr key={line.id}>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">
                              {line.productSku || line.InventoryItem?.product_sku || 'SKU'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {line.productName || line.InventoryItem?.product_name || 'Item'}
                            </div>
                            <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                              {requestedIdentifiers.length > 0 && (
                                <div>Requested: {requestedIdentifiers.join(', ')}</div>
                              )}
                              {projectLabel ? (
                                <div>Project: {projectLabel}</div>
                              ) : (
                                <div>Pick rule: FIFO non-project stock first; project stock fallback if needed.</div>
                              )}
                              {Number(packageQuantities[line.id] || 0) > 0 && !lineHasWeight && (
                                <div className="font-medium text-amber-700">Missing item weight source data.</div>
                              )}
                              {Number(packageQuantities[line.id] || 0) > 0 && !lineHasDimensions && (
                                <div className="font-medium text-amber-700">Missing item dimension source data.</div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{requestedQty}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{remainingQty}</td>
                          <td className="px-4 py-3 text-right">
                            <input
                              type="number"
                              min="0"
                              max={remainingQty}
                              value={packageQuantities[line.id] ?? 0}
                              onChange={(e) => {
                                const value = Math.max(0, Math.min(remainingQty, Number(e.target.value || 0)));
                                setPackageQuantities((current) => ({
                                  ...current,
                                  [line.id]: value
                                }));
                              }}
                              className="w-24 rounded-md border border-gray-300 px-3 py-2 text-right text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowPackageModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="button"
                disabled={createPackageMutation.isLoading}
                onClick={() => {
                  const lineItems = requestLineItems
                    .map((line) => ({
                      requestLineItemId: line.id,
                      quantity: Number(packageQuantities[line.id] || 0)
                    }))
                    .filter((line) => line.quantity > 0);

                  if (lineItems.length === 0) {
                    toast.error('Package must include at least one item');
                    return;
                  }

                  const requiresPackageDetails = (request.shippingMethod || request.shipping_method) !== 'pickup';
                  const numericPackageDetails = {
                    package_type: packageDetails.package_type,
                    weight_lbs: packageDetails.weight_lbs === '' ? null : Number(packageDetails.weight_lbs),
                    length: packageDetails.length === '' ? null : Number(packageDetails.length),
                    width: packageDetails.width === '' ? null : Number(packageDetails.width),
                    height: packageDetails.height === '' ? null : Number(packageDetails.height)
                  };

                  if (requiresPackageDetails && (!numericPackageDetails.weight_lbs || numericPackageDetails.weight_lbs <= 0)) {
                    toast.error('Package weight must be greater than zero');
                    return;
                  }
                  if (requiresPackageDetails && (!numericPackageDetails.length || !numericPackageDetails.width || !numericPackageDetails.height || numericPackageDetails.length <= 0 || numericPackageDetails.width <= 0 || numericPackageDetails.height <= 0)) {
                    toast.error('Package length, width, and height must be greater than zero');
                    return;
                  }

                  createPackageMutation.mutate({
                    ...numericPackageDetails,
                    lineItems
                  });
                }}
                className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {createPackageMutation.isLoading ? 'Creating...' : 'Create Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4" role="dialog" aria-modal="true" aria-labelledby="edit-package-modal-title">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 id="edit-package-modal-title" className="text-lg font-semibold text-gray-900">
                Edit Package Details
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Update package label, type, weight, and dimensions before this package is linked to a shipment.
              </p>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="edit-package-label" className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Label
                  </label>
                  <input
                    id="edit-package-label"
                    type="text"
                    value={editPackageDetails.package_label}
                    onChange={(e) => setEditPackageDetails((current) => ({
                      ...current,
                      package_label: e.target.value
                    }))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label htmlFor="edit-package-type" className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Type
                  </label>
                  <select
                    id="edit-package-type"
                    value={editPackageDetails.package_type}
                    onChange={(e) => setEditPackageDetails((current) => ({
                      ...current,
                      package_type: e.target.value
                    }))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="box">Box</option>
                    <option value="pallet">Pallet</option>
                    <option value="envelope">Envelope</option>
                    <option value="tube">Tube</option>
                    <option value="crate">Crate</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {[
                  ['weight_lbs', 'Weight (lb)'],
                  ['length', 'Length (in)'],
                  ['width', 'Width (in)'],
                  ['height', 'Height (in)']
                ].map(([field, label]) => (
                  <div key={field}>
                    <label htmlFor={`edit-package-${field}`} className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                      {label}
                    </label>
                    <input
                      id={`edit-package-${field}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={editPackageDetails[field]}
                      onChange={(e) => setEditPackageDetails((current) => ({
                        ...current,
                        [field]: e.target.value
                      }))}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setEditingPackage(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="button"
                disabled={updatePackageMutation.isLoading}
                onClick={() => {
                  const requiresPackageDetails = (request.shippingMethod || request.shipping_method) !== 'pickup';
                  const payload = {
                    package_label: editPackageDetails.package_label.trim() || null,
                    package_type: editPackageDetails.package_type,
                    weight_lbs: editPackageDetails.weight_lbs === '' ? null : Number(editPackageDetails.weight_lbs),
                    length: editPackageDetails.length === '' ? null : Number(editPackageDetails.length),
                    width: editPackageDetails.width === '' ? null : Number(editPackageDetails.width),
                    height: editPackageDetails.height === '' ? null : Number(editPackageDetails.height)
                  };

                  if (requiresPackageDetails && (!payload.weight_lbs || payload.weight_lbs <= 0)) {
                    toast.error('Package weight must be greater than zero');
                    return;
                  }
                  if (requiresPackageDetails && (!payload.length || !payload.width || !payload.height || payload.length <= 0 || payload.width <= 0 || payload.height <= 0)) {
                    toast.error('Package length, width, and height must be greater than zero');
                    return;
                  }

                  updatePackageMutation.mutate({
                    packageId: editingPackage.id,
                    payload
                  });
                }}
                className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {updatePackageMutation.isLoading ? 'Saving...' : 'Save Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Packages Tab */}
      {activeTab === 'packages' && hasOutboundPackages && (
        <div className="mb-6 overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="border-b border-gray-200 px-4 py-5 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">Packages</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Packed items reserved for this outbound request.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {canCreateShipmentFromPackages && (
                  <button
                    type="button"
                    disabled={createShipmentFromPackagesMutation.isLoading}
                    onClick={() => {
                      if (!isPickupRequest) {
                        const packageSummary = selectedShippablePackages
                          .map((pkg) => `${pkg.package_label || `Package ${pkg.package_number}`}: ${formatPackageMeasurements(pkg)}`)
                          .join('\n');
                        const confirmed = window.confirm(
                          `Confirm carrier shipment package details before using the standard shipping portal:\n\n${packageSummary}\n\nCreate shipment record from these ${selectedShipmentPackageCount} package(s)?`
                        );
                        if (!confirmed) return;
                      }
                      createShipmentFromPackagesMutation.mutate(selectedShippablePackages.map((pkg) => pkg.id));
                    }}
                    className="inline-flex items-center justify-center rounded-md border border-green-700 bg-green-700 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {createShipmentFromPackagesMutation.isLoading ? 'Creating...' : `Create Shipment (${selectedShippablePackages.length})`}
                  </button>
                )}
                {canCreatePackage && (
                  <button
                    type="button"
                    onClick={openPackageModal}
                    className="inline-flex items-center justify-center rounded-md border border-brand-navy bg-brand-navy px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-navy/90"
                  >
                    Create Package
                  </button>
                )}
              </div>
            </div>
            {hasShippablePackages && shippablePackagesMissingCarrierDetails.length > 0 && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                Add weight and dimensions to {shippablePackagesMissingCarrierDetails.length} packed package(s) before creating a carrier shipment record.
              </div>
            )}
            {canCreateShipmentFromPackages && !isPickupRequest && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Carrier label/rate purchase is not wired in this app yet. Create the shipment record here, then book carrier service in the standard shipping portal.
              </div>
            )}
            {hasShippablePackages && (
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <span>{selectedShippablePackages.length} of {shippablePackages.length} shippable package(s) selected.</span>
                <span>{selectedShipmentItemCount} item(s) selected.</span>
                <button
                  type="button"
                  onClick={() => setSelectedShipmentPackageIds(shippablePackageIds)}
                  className="font-medium text-indigo-700 hover:text-indigo-900"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedShipmentPackageIds([])}
                  className="font-medium text-indigo-700 hover:text-indigo-900"
                >
                  Select none
                </button>
              </div>
            )}
            {selectedShippablePackages.length > 0 && (
              <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                <p className="font-semibold text-gray-800">Selected shipment packages</p>
                <div className="mt-1 grid gap-1 sm:grid-cols-2">
                  {selectedShippablePackages.map((pkg) => (
                    <div key={pkg.id}>
                      {pkg.package_label || `Package ${pkg.package_number}`}: {formatPackageMeasurements(pkg)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="divide-y divide-gray-200">
            {outboundPackages.map((pkg) => (
              <div key={pkg.id} className="px-4 py-5 sm:px-6">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    {pkg.status === 'packed' && !pkg.shipment_id && (
                      <input
                        type="checkbox"
                        checked={selectedShipmentPackageIds.includes(pkg.id)}
                        onChange={(e) => {
                          setSelectedShipmentPackageIds((current) => (
                            e.target.checked
                              ? Array.from(new Set([...current, pkg.id]))
                              : current.filter((packageId) => packageId !== pkg.id)
                          ));
                        }}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        aria-label={`Select ${pkg.package_label || `Package ${pkg.package_number}`} for shipment`}
                      />
                    )}
                    <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      {pkg.package_label || `Package ${pkg.package_number}`}
                    </h4>
                    <p className="text-sm text-gray-500">
                      Status: {formatStatus(pkg.status)}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {pkg.package_type || 'box'} · {pkg.weight_lbs || '-'} lb · {pkg.length || '-'} x {pkg.width || '-'} x {pkg.height || '-'} in
                    </p>
                    {pkg.status === 'packed' && !pkg.shipment_id && !isPickupRequest && !packageHasCarrierDetails(pkg) && (
                      <p className="mt-1 text-sm font-medium text-red-700">
                        Missing carrier weight or dimensions
                      </p>
                    )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {(pkg.LineItems || []).reduce((total, line) => total + Number(line.quantity || 0), 0)} item(s)
                  </div>
                </div>
                {pkg.status === 'packed' && !pkg.shipment_id && (
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      disabled={updatePackageMutation.isLoading}
                      onClick={() => openEditPackageModal(pkg)}
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                    >
                      Edit Package
                    </button>
                    <button
                      type="button"
                      disabled={cancelPackageMutation.isLoading}
                      onClick={() => {
                        const reason = window.prompt('Reason for cancelling this package?');
                        if (reason === null) return;
                        cancelPackageMutation.mutate({
                          packageId: pkg.id,
                          reason: reason.trim() || null
                        });
                      }}
                      className="inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-400"
                    >
                      Cancel Package
                    </button>
                  </div>
                )}
                <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">SKU</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Item</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Serial/MAC</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Qty</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {(pkg.LineItems || []).filter((line) => line.status !== 'cancelled' && Number(line.quantity || 0) > 0).map((line) => {
                        const assetProject = formatLineProject(line.InventoryAsset);
                        const reservationSource = formatReservationSource(line.metadata?.reservation_source);
                        return (
                          <tr key={line.id}>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">
                              {line.RequestLineItem?.productSku || line.RequestLineItem?.InventoryItem?.product_sku || '-'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              {line.RequestLineItem?.productName || line.RequestLineItem?.InventoryItem?.product_name || '-'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              <div>{formatAssetIdentifier(line.InventoryAsset)}</div>
                              {assetProject && (
                                <div className="text-xs text-gray-500">Project: {assetProject}</div>
                              )}
                              {reservationSource && (
                                <div className="text-xs text-gray-500">Source: {reservationSource}</div>
                              )}
                              {line.metadata?.warning && (
                                <div className="text-xs font-medium text-amber-700">{line.metadata.warning}</div>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right text-sm text-gray-700">{line.quantity}</td>
                            <td className="px-4 py-2 text-right">
                            {pkg.status === 'packed' && !pkg.shipment_id && Number(line.quantity || 0) > 0 ? (
                              <button
                                type="button"
                                disabled={updatePackageLineMutation.isLoading}
                                onClick={() => {
                                  const currentQuantity = Number(line.quantity || 0);
                                  const value = window.prompt('New package quantity? Enter a lower number to release inventory.', String(currentQuantity));
                                  if (value === null) return;
                                  const quantity = Number(value);
                                  if (!Number.isInteger(quantity) || quantity < 0) {
                                    toast.error('Quantity must be a whole number zero or greater');
                                    return;
                                  }
                                  if (quantity > currentQuantity) {
                                    toast.error('Increasing quantity is not supported here. Create another package for additional items.');
                                    return;
                                  }
                                  if (quantity === currentQuantity) return;
                                  const reason = window.prompt('Reason for reducing this package line? Optional.');
                                  if (reason === null) return;
                                  updatePackageLineMutation.mutate({
                                    packageId: pkg.id,
                                    lineItemId: line.request_line_item_id,
                                    payload: {
                                      quantity,
                                      reason: reason.trim() || null
                                    }
                                  });
                                }}
                                className="text-sm font-medium text-indigo-700 hover:text-indigo-900 disabled:cursor-not-allowed disabled:text-gray-400"
                              >
                                Reduce Qty
                              </button>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shipment Tab */}
      {activeTab === 'shipment' && hasShipment && (
        <div className="mb-6 space-y-4">
          {requestShipments.length > 1 && (
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Shipments</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">{requestShipments.length}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Packages</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {requestShipments.reduce((total, shipment) => total + getShipmentPackageCount(shipment), 0)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Items</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {requestShipments.reduce((total, shipment) => total + getShipmentItemCount(shipment), 0)}
                </p>
              </div>
            </div>
          )}
          {requestShipments.map((shipment, index) => (
            <div key={shipment.id || index} className="space-y-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  Shipment {index + 1}{shipment.shipment_number ? ` · ${shipment.shipment_number}` : ''}
                </h3>
                <p className="text-xs text-gray-500">
                  {getShipmentPackageCount(shipment)} package(s) · {getShipmentItemCount(shipment)} item(s) · {formatStatus(shipment.status)}
                </p>
              </div>
              <ShipmentPanel
                shipment={shipment}
                requestId={request.id}
                requestShippingMethod={request.shippingMethod || request.shipping_method || null}
                onUpdate={refetch}
                isAdmin={true}
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/shipping/shipments/${shipment.id}/packing-slip`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                      });
                      const blob = await response.blob();
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank');
                    } catch (err) {
                      toast.error('Failed to load packing slip');
                    }
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 shadow-sm"
                >
                  Print Packing Slip
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Shipment Groups Tab */}
      {activeTab === 'shipment_groups' && hasShipmentGroups && (
        <div className="mb-6 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Shipment Groups</h3>
              <p className="mt-1 text-sm text-gray-500">
                Per-box routing labels for this shipment. Each group generates one sticker per box at print time.
              </p>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const html = await adminAPI.getShipmentLabels(request.id);
                  const win = window.open('', '_blank');
                  if (win) {
                    win.document.write(html);
                    win.document.close();
                  }
                } catch (err) {
                  alert(err.response?.data || 'Failed to load label sheet');
                }
              }}
              className="inline-flex items-center rounded-md border border-brand-navy bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy/90"
            >
              Print Labels
            </button>
          </div>
          <div className="border-t border-gray-200">
            <ul className="divide-y divide-gray-200">
              {request.shipmentGroups.map((group, idx) => (
                <li key={idx} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        {group.project_code && (
                          <span className="text-xs font-semibold uppercase tracking-wide text-brand-gold">
                            {group.project_code}
                          </span>
                        )}
                        {group.project_name && (
                          <span className="text-sm text-gray-500">{group.project_name}</span>
                        )}
                      </div>
                      <div className="mt-1 text-base font-semibold text-brand-navy">
                        {group.room_location || '[no room specified]'}
                      </div>
                      {group.recipient_name && (
                        <div className="mt-1 text-sm text-gray-700">Attn: {group.recipient_name}</div>
                      )}
                      {group.recipient_notes && (
                        <div className="mt-1 text-sm text-gray-500">{group.recipient_notes}</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-brand-navy">{group.box_count ?? 1}</div>
                      <div className="text-xs text-gray-500">box{(group.box_count ?? 1) === 1 ? '' : 'es'}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Receiving History Tab (inbound requests) */}
      {activeTab === 'receiving_history' && request && (
        <ReceivingHistoryPanel
          requestId={request.id}
          requestType={request.requestType}
          manifestId={manifestData?.manifest?.id}
          onVoided={() => {
            refetch();
            queryClient.invalidateQueries(['manifest-for-request', id]);
          }}
        />
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && request && (
        <>
          <DocumentsTab requestId={request.id} />
          <div className="mt-6">
            <PhotosTab requestId={request.id} />
          </div>
        </>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && request && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Billing Summary for {request.requestNumber || 'Request'}
            </h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            {/* pick_pack and kit_build are not auto-billed under the
                simplified catalog -- ops invoices them manually (RF9).
                Other types keep the automatic billing summary. */}
            {['pick_pack', 'kit_build'].includes(request.requestType) ? (
              <RequestCostsManager
                request={request}
                isAdmin={true}
                onInvoiceCreated={handleInvoiceCreated}
              />
            ) : (
              <RequestBillingManager
                request={request}
                onInvoiceCreated={handleInvoiceCreated}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === 'comments' && request && (
        <RequestCommentsPanel requestId={request.id} />
      )}

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <ActivityTimeline
              fetchFn={(params) => adminAPI.getEntityTimeline('Request', id, params)}
              entityType="Request"
              entityId={id}
              limit={25}
              title={null}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.73 0L3.084 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Request</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this request? This action cannot be undone.
                </p>
                {deleteStatus?.transactionDetails && deleteStatus.transactionDetails.length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-xs text-red-600 font-medium">Cannot delete:</p>
                    <ul className="text-xs text-red-600 mt-1">
                      {deleteStatus.transactionDetails.map((detail, index) => (
                        <li key={index}>• {detail}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    deleteRequestMutation.mutate(id);
                    setShowDeleteConfirm(false);
                  }}
                  disabled={!deleteStatus?.canDelete || deleteRequestMutation.isLoading}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteRequestMutation.isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manifest Receiving Modal */}
      <ManifestReceivingModal
        isOpen={showReceivingModal}
        onClose={() => setShowReceivingModal(false)}
        manifestData={manifestData}
        requestId={id}
      />
    </div>
  );
};

export default AdminRequestDetail; 
